import { jsxs, jsx } from "react/jsx-runtime";
import * as React from "react";
import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { CellActivitySpinner } from "../../common/UI/Loaders/Activity.js";
import ChevronDown from "../../common/assets/icons/ChevronDown.js";
import { useBulkRowSelection } from "../../context/useBulkRowSelection.js";
import { useKeyActionsContext } from "../../context/useKeyActions.js";
import { SelectedCellStyle, getRowsAreSelected, cellIsSelected } from "../../hooks/useSelectCells.js";
import { DIFF_MESSAGES_LENGTH } from "../../utils/cells.js";
import { named } from "../../utils/named.js";
import { useOnClickOutside } from "@flatfile/design-system";
import { useTranslation } from "react-i18next";
import { useClipboardActionsContext } from "../../context/useClipboardActions.js";
import { useEditorContext } from "../../context/useEditorContext.js";
import { isElementCloseAddNewRow, isElementInNewRowWrapper } from "../../utils/add-rows.js";
import { EditorContainer } from "./EditorContainer.js";
import { BasicCellWrapper, BasicCellInner, DropdownIcon } from "./Styles.js";
import Tooltip from "./Tooltip.js";
const MemoizedBasicCellWrapper = React.memo(BasicCellWrapper);
const EMPTY = {};
const computeCanEdit = ({
  active,
  isActiveGhostRow,
  readOnly,
  editorDisabled,
  status
}) => !(status == null ? void 0 : status.disabled) && !active && !isActiveGhostRow && !readOnly && !editorDisabled;
const getTooltipMessages = (status, cell) => {
  const messages = [];
  if (!(cell == null ? void 0 : cell.status))
    return messages;
  if (cell.status.error) {
    messages.push(...cell.status.error.map((e) => e.message));
  }
  if (cell.status.warning) {
    messages.push(...cell.status.warning.map((e) => e.message));
  }
  if (cell.status.info)
    messages.push(...cell.status.info.map((e) => e.message));
  return messages;
};
const CellContainer = (props) => {
  var _a, _b, _c;
  const {
    active,
    cell,
    children,
    column,
    columnConfig,
    dropdown,
    innerStatus,
    isFirstColumn,
    isFirstNonHeaderRow,
    isFocused,
    isGhostRow,
    isLastRow,
    isLastColumn,
    isSelected,
    isBoolean,
    editor,
    decorateCell,
    mutated,
    number,
    onCellArrowDown,
    onCellArrowLeft,
    onCellArrowRight,
    onCellArrowUp,
    onCellFocus,
    onCellTab,
    onClick,
    onMouseDown,
    onMouseEnter,
    onMouseUp,
    onSelectedRows,
    onUpdateCell,
    onEnterOrSpace,
    pinned,
    row,
    rowData,
    scrollToNextRow,
    scrollToPreviousRow,
    status,
    style,
    tableId,
    theme,
    tooltipDisabled,
    totalRows,
    totalColumns,
    cellContextMenuSlot,
    className,
    isNewRowCell
  } = props;
  const DecorateCellComponent = decorateCell;
  const cellId = `${rowData == null ? void 0 : rowData.id}:${columnConfig == null ? void 0 : columnConfig.index}`;
  const cellRef = useRef(null);
  const { onKeyDown } = useKeyActionsContext();
  const { activeEditor, setActiveEditor } = useEditorContext();
  const { rowIsSelected, exitSelectionState, getIsBulkSelecting } = useBulkRowSelection();
  const [hovered, setIsHovered] = useState(false);
  const [focused, setIsFocused] = useState(isFocused);
  const contextMenuOpen = useRef(false);
  const [editorState, setEditorState] = useState({});
  const [disableSelection, setDisableSelection] = useState(true);
  const { onCopy, onPaste, onCut } = useClipboardActionsContext();
  const { t } = useTranslation();
  const isBulkSelecting = getIsBulkSelecting && getIsBulkSelecting();
  const isEditing = activeEditor && activeEditor.row === row && activeEditor.column === column;
  const isHidden = isGhostRow && !isBoolean && !isNewRowCell;
  const editingEnabled = computeCanEdit(props);
  const tooltipMessages = useMemo(() => {
    var _a2, _b2;
    if (isEditing || tooltipDisabled)
      return;
    const diffMessages = ((_b2 = (_a2 = cell == null ? void 0 : cell.status) == null ? void 0 : _a2.diff) == null ? void 0 : _b2.map((msg) => msg.message)) ?? [];
    const metaMessages = (status == null ? void 0 : status.disabled) ? [t("sheet.table.cells.messages.readOnly")] : [];
    const messages = getTooltipMessages(status, cell);
    if (!messages.length && !metaMessages.length && diffMessages.length !== DIFF_MESSAGES_LENGTH) {
      return;
    }
    return { messages, metaMessages, diffMessages };
  }, [cell == null ? void 0 : cell.status, status, isEditing, tooltipDisabled]);
  const innerCellStatus = innerStatus || status || EMPTY;
  const positioningProps = useMemo(
    () => ({
      isLastRow,
      isLastColumn,
      isFirstColumn,
      isFirstNonHeaderRow
    }),
    [isLastRow, isLastColumn, isFirstColumn, isFirstNonHeaderRow]
  );
  const styles = useMemo(() => {
    if (isSelected || rowIsSelected && rowData && rowIsSelected(rowData)) {
      return { ...style, ...SelectedCellStyle(theme) };
    }
    return style;
  }, [isSelected, style, rowIsSelected, rowData]);
  const handleSetEditorState = useCallback(
    (meta, row2, column2) => {
      if (editingEnabled && row2 !== void 0 && column2 !== void 0) {
        setEditorState(meta);
        setActiveEditor({ row: row2, column: column2 });
        setTimeout(() => {
          setDisableSelection(false);
        }, 100);
      } else {
        setEditorState(meta);
        setActiveEditor(null);
        setDisableSelection(true);
      }
    },
    [editingEnabled]
  );
  const handleDoubleClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (exitSelectionState)
        exitSelectionState();
      handleSetEditorState(
        { data: { value: cell == null ? void 0 : cell.value, diffValue: cell == null ? void 0 : cell.diffValue } },
        row,
        column
      );
    },
    [cell, exitSelectionState]
  );
  const handleMouseDown = useCallback(
    (event) => {
      var _a2;
      if (event.button === 2) {
        contextMenuOpen.current = true;
        return;
      }
      if (!isBulkSelecting) {
        onMouseDown(cellRef, event.button, event.shiftKey);
        (_a2 = cellRef.current) == null ? void 0 : _a2.focus();
      }
      if (pinned || isBulkSelecting) {
        onCellFocus(cellRef, contextMenuOpen.current);
        setIsFocused(true);
      }
      if (onSelectedRows) {
        if (getRowsAreSelected(totalColumns, tableId)) {
          onSelectedRows([]);
        }
      }
    },
    [cellRef, pinned, isBulkSelecting]
  );
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (isBulkSelecting)
      return;
    onMouseEnter(cellRef);
  }, [cellRef, isBulkSelecting, setIsHovered]);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, [isSelected]);
  const handleMouseUp = useCallback(() => {
    if (isBulkSelecting)
      return;
    onMouseUp(cellRef);
  }, [cellRef, isBulkSelecting]);
  const handleKeydown = useCallback(
    (event) => {
      var _a2;
      if (event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      if (event.key === "Tab") {
        if (scrollToNextRow || scrollToPreviousRow || isEditing || pinned || isFirstColumn) {
          event.preventDefault();
          onCellTab(cellRef, event.shiftKey);
          if (scrollToNextRow && !event.shiftKey) {
            scrollToNextRow();
          }
          if (scrollToPreviousRow && event.shiftKey)
            scrollToPreviousRow();
        }
        setIsFocused(false);
        return;
      }
      if (onEnterOrSpace && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        event.stopPropagation();
        onEnterOrSpace();
        return;
      }
      if (!isEditing) {
        event.preventDefault();
        event.stopPropagation();
        if (editingEnabled && (event.key.length === 1 || event.key === "Enter")) {
          if (exitSelectionState)
            exitSelectionState();
          const keyPressValue = event.key === "Enter" ? void 0 : event.key;
          handleSetEditorState(
            {
              keyPressValue,
              data: { value: cell == null ? void 0 : cell.value }
            },
            row,
            column
          );
          if (event.key === "Enter") {
            if (props.isNewRowCell) {
              (_a2 = props.onAddNewRow) == null ? void 0 : _a2.call(props);
            }
          }
          return;
        }
        if (event.key === "Delete" || event.key === "Backspace" || event.key === "Escape") {
          onKeyDown(event);
          return;
        }
        if (event.key === "ArrowRight") {
          onCellArrowRight(cellRef);
          return;
        }
        if (event.key === "ArrowLeft") {
          onCellArrowLeft(cellRef);
          return;
        }
        if (event.key === "ArrowDown") {
          onCellArrowDown(cellRef);
          return;
        }
        if (event.key === "ArrowUp") {
          onCellArrowUp(cellRef);
          return;
        }
      }
    },
    [cell, cellRef, isEditing, editingEnabled, pinned, exitSelectionState]
  );
  const handleFocus = useCallback(() => {
    if (!isBulkSelecting) {
      onCellFocus(cellRef, contextMenuOpen.current);
    }
    setIsFocused(true);
  }, [cellRef, onCellFocus, isBulkSelecting]);
  const handleBlur = useCallback((event) => {
    setIsFocused(false);
  }, []);
  useEffect(() => {
    var _a2;
    if (isFocused && !isEditing) {
      (_a2 = cellRef.current) == null ? void 0 : _a2.focus({ preventScroll: true });
    }
  }, [isFocused]);
  const handleClickOutside = useCallback(
    (event) => {
      var _a2;
      if (isFocused && !isEditing && props.isNewRowCell && event) {
        const target = event.target;
        const isCloseButton = isElementCloseAddNewRow(target);
        if (isCloseButton) {
          return;
        }
        const isClickedInsideNewRowWrapper = isElementInNewRowWrapper(target);
        if (!isClickedInsideNewRowWrapper) {
          (_a2 = props.onAddNewRow) == null ? void 0 : _a2.call(props);
        }
      }
    },
    [isEditing, props.isNewRowCell, props.onAddNewRow]
  );
  useOnClickOutside(cellRef, handleClickOutside);
  const content = /* @__PURE__ */ jsxs(
    MemoizedBasicCellWrapper,
    {
      ...positioningProps,
      ...status,
      ref: cellRef,
      "data-cell-wrapper": "BasicCellWrapper",
      role: "cell",
      style: styles,
      tabIndex: 1,
      isFocused: focused && !isEditing,
      isEditing,
      pinned,
      hasTooltip: !!tooltipMessages,
      showFocus: isFocused && (active || contextMenuOpen.current),
      disableSelection,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeydown,
      onMouseDown: handleMouseDown,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onMouseUp: handleMouseUp,
      onClick,
      onDoubleClick: editingEnabled && !isEditing ? handleDoubleClick : void 0,
      className,
      "data-new-row-cell": isNewRowCell,
      children: [
        /* @__PURE__ */ jsxs(
          BasicCellInner,
          {
            ...innerCellStatus,
            hidden: isHidden,
            number,
            boolean: isBoolean,
            children: [
              children,
              dropdown && !active && /* @__PURE__ */ jsx(DropdownIcon, { onClick: handleDoubleClick, children: /* @__PURE__ */ jsx(ChevronDown, {}) }),
              /* @__PURE__ */ jsx(CellActivitySpinner, { show: !!active, left: number })
            ]
          }
        ),
        tooltipMessages && /* @__PURE__ */ jsx(
          Tooltip,
          {
            ...positioningProps,
            ...tooltipMessages,
            cell,
            row,
            status,
            totalRows,
            rowHeight: theme.rowHeight
          }
        ),
        editingEnabled && isEditing && /* @__PURE__ */ jsx(
          EditorContainer,
          {
            ...editorState,
            ...positioningProps,
            cell,
            columnConfig,
            editor,
            isEditing,
            status,
            mutated,
            cellRef,
            isGhostRow,
            onChangeEditorState: handleSetEditorState,
            onUpdateCell,
            onCellArrowDown,
            onCellArrowUp,
            isNewRowCell: props.isNewRowCell,
            rowData: props.rowData
          }
        ),
        !isGhostRow && DecorateCellComponent && /* @__PURE__ */ jsx(
          DecorateCellComponent,
          {
            id: cellId,
            isSelected,
            isActive: active ?? false,
            isFocused: focused ?? false,
            isHovered: hovered ?? false,
            isEditing: isEditing ?? false,
            isGhostRow: isGhostRow ?? false,
            isBoolean: isBoolean ?? false,
            isHidden: isHidden ?? false,
            isLastRow: isLastRow ?? false,
            isLastColumn: isLastColumn ?? false,
            isFirstColumn: isFirstColumn ?? false,
            isFirstNonHeaderRow: isFirstNonHeaderRow ?? false,
            isRowHeader: false,
            columnIndex: column,
            requiresDecision: (_a = cell == null ? void 0 : cell.metadata) == null ? void 0 : _a.isDecisioning,
            isUnresolvedDecision: ((_b = cell == null ? void 0 : cell.metadata) == null ? void 0 : _b.isDecisioning) && !((_c = cell == null ? void 0 : cell.metadata) == null ? void 0 : _c.acceptedValue)
          }
        )
      ]
    }
  );
  const handleContextMenuInterferrance = (open) => {
    contextMenuOpen.current = open;
    if (!open) {
      setIsFocused(false);
      return;
    }
    if (!isBulkSelecting && !cellIsSelected([row, column], totalColumns, tableId)) {
      onCellFocus(cellRef);
    }
  };
  return cellContextMenuSlot ? cellContextMenuSlot({
    handleCopy: onCopy,
    handlePaste: onPaste,
    handleCut: onCut,
    onOpenChange: handleContextMenuInterferrance,
    children: content,
    editingEnabled,
    cellId: (rowData == null ? void 0 : rowData.id) ? cellId : void 0,
    isFocused: isFocused && cellRef.current === document.activeElement
  }) : content;
};
named("CellContainer", CellContainer);
export {
  computeCanEdit,
  CellContainer as default
};
